/**
 * The rules for which exercises belong to a combo set (exercises done back to back as one group).
 *
 * A combo set is the run of ADJACENT exercises sharing one `comboSetNumber`; an exercise with no
 * number is an individual. The exercise list applies these rules when a drag ends, and the drag
 * service applies the same rules to draw its live preview, so the two cannot disagree.
 *
 * Every function is pure: it reads only `comboSetNumber`, never changes its input, and returns the
 * new number for each item by index. The caller writes those numbers back.
 */
export interface ComboSetItem {
  comboSetNumber?: number | undefined;
}

export type ComboSetNumbers = (number | undefined)[];

export type ComboSetPosition = 'first' | 'middle' | 'last';

// the combo set number of each item, by index, as a fresh array the caller may change
function numbersOf(items: readonly ComboSetItem[]): ComboSetNumbers {
  return items.map(item => item.comboSetNumber);
}

// for an item in a combo set, whether the item before / after it is in the same combo set.
// an individual exercise (no number) has no same-group neighbour on either side.
function sameGroupNeighbours(numbers: readonly (number | undefined)[], index: number): { prev: boolean, next: boolean } {
  const own = numbers[index];
  if (own === undefined) return { prev: false, next: false };
  return {
    prev: index > 0 && numbers[index - 1] === own,
    next: index < numbers.length - 1 && numbers[index + 1] === own
  };
}

// where the item sits in its combo set, for the bracket drawn beside it. null for an individual
// exercise, and for a lone member that has not been dissolved yet.
export function comboSetPosition(items: readonly ComboSetItem[], index: number): ComboSetPosition | null {
  if (index < 0 || index >= items.length) return null;
  const { prev, next } = sameGroupNeighbours(numbersOf(items), index);
  if (!prev && next) return 'first';
  if (prev && next) return 'middle';
  if (prev && !next) return 'last';
  return null;
}

// a card dropped onto another card: if the target is in a combo set the dragged exercise joins
// it, otherwise the two become a new combo set numbered above every existing one.
export function joinComboSet(items: readonly ComboSetItem[], draggedIndex: number, targetIndex: number): ComboSetNumbers {
  const numbers = numbersOf(items);
  if (!items[draggedIndex] || !items[targetIndex]) return numbers;

  const targetNumber = numbers[targetIndex];
  if (targetNumber !== undefined) {
    numbers[draggedIndex] = targetNumber;
  } else {
    const maxNumber = numbers.reduce<number>((max, n) => n !== undefined ? Math.max(max, n) : max, 0);
    numbers[draggedIndex] = maxNumber + 1;
    numbers[targetIndex] = maxNumber + 1;
  }
  return numbers;
}

/**
 * The grouping after a drag ends, in this order:
 * 1. if the dragged exercise landed between two members of one combo set, it joins that set.
 *    only the dragged exercise does this; others that end up between members by the reorder do not.
 * 2. if this was NOT a card-on-card drop and the dragged exercise sits at the edge of its own
 *    combo set (exactly one same-group neighbour), it leaves the set. this is how a user drags an
 *    exercise out of a combo set.
 * 3. any member left with no same-group neighbour becomes an individual exercise.
 * The numbers are not made sequential here; renumberComboSets does that.
 */
export function settleComboSetsAfterDrag(items: readonly ComboSetItem[], draggedIndex: number, wasComboSetDrop: boolean): ComboSetNumbers {
  const numbers = numbersOf(items);
  const draggedInRange = draggedIndex >= 0 && draggedIndex < numbers.length;

  // step 1: auto-join when dropped between two members of the same combo set
  if (draggedInRange) {
    const prev = draggedIndex > 0 ? numbers[draggedIndex - 1] : undefined;
    const next = draggedIndex < numbers.length - 1 ? numbers[draggedIndex + 1] : undefined;
    if (prev !== undefined && prev === next && numbers[draggedIndex] !== prev) {
      numbers[draggedIndex] = prev;
    }
  }

  // step 2: leave the combo set when dropped at its edge
  if (draggedInRange && !wasComboSetDrop) {
    const { prev, next } = sameGroupNeighbours(numbers, draggedIndex);
    if (prev !== next) {
      numbers[draggedIndex] = undefined;
    }
  }

  // step 3: dissolve lone members. reads the numbers as they are being updated, left to right,
  // which is what the in-place loop this replaced did.
  for (let i = 0; i < numbers.length; i++) {
    if (numbers[i] === undefined) continue;
    const { prev, next } = sameGroupNeighbours(numbers, i);
    if (!prev && !next) {
      numbers[i] = undefined;
    }
  }

  return numbers;
}

// renumber the combo sets 1, 2, 3... keeping their relative order by old number
export function renumberComboSets(items: readonly ComboSetItem[]): ComboSetNumbers {
  const numbers = numbersOf(items);
  const uniqueNumbers = [...new Set(numbers.filter((n): n is number => n !== undefined))].sort((a, b) => a - b);
  const numberMap = new Map<number, number>();
  uniqueNumbers.forEach((oldNumber, index) => numberMap.set(oldNumber, index + 1));
  return numbers.map(n => n !== undefined ? numberMap.get(n) : undefined);
}
